/**
 * CommunityLeaderboardPage — premium leaderboards.
 *
 * Each entry links to the public profile page (already privacy-safe).
 * Top 3 get a podium hero with gradient avatars, medals, and the metric
 * highlighted prominently. The rest of the table is a clean ranked list.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, Crown, Shield, Gem, Loader2, Users, UserPlus, Trophy,
  Flame, Target, MessageSquare, DollarSign, Sparkles,
} from 'lucide-react';
import { getLeaderboard, followUser, unfollowUser, checkFollowing } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const AVATAR_GRADIENTS = [
  'from-rose-500 to-pink-600', 'from-purple-500 to-fuchsia-600',
  'from-violet-500 to-indigo-600', 'from-blue-500 to-cyan-600',
  'from-emerald-500 to-teal-600', 'from-amber-500 to-orange-600',
];
function gradientFor(name) {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
}

// Strip anything that looks like an email prefix or includes "@", and fall
// back to a deterministic Trader-XXXXXX handle if the registry leaks an email.
function safeName(entry) {
  const n = (entry.userName || '').trim();
  if (n && !n.includes('@')) return n;
  const id = String(entry.userId || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase();
  return id ? `Trader-${id}` : 'Trader';
}

const ROLE_BADGE = {
  owner:      { cls: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'OWNER', icon: Crown },
  superadmin: { cls: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'S.ADMIN', icon: Shield },
  admin:      { cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'ADMIN', icon: Shield },
  mod:        { cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', label: 'MOD', icon: Shield },
  vip:        { cls: 'bg-violet-500/20 text-violet-400 border-violet-500/30', label: 'VIP', icon: Gem },
};

const TIER_BADGE = {
  trial:   'bg-gray-500/15 text-gray-400 border-gray-500/30',
  starter: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  trader:  'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  pro:     'bg-violet-500/15 text-violet-400 border-violet-500/30',
  elite:   'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 border-amber-500/30',
};

const CATEGORIES = [
  { key: 'most_active',    label: 'Most Active',    icon: MessageSquare, emoji: '💬', unit: 'messages', color: 'from-violet-500/15 to-purple-500/15 border-violet-500/30 text-violet-300' },
  { key: 'top_callers',    label: 'Top Callers',    icon: Target,        emoji: '🎯', unit: 'accuracy', color: 'from-emerald-500/15 to-teal-500/15 border-emerald-500/30 text-emerald-300' },
  { key: 'best_returns',   label: 'Best Returns',   icon: DollarSign,    emoji: '💰', unit: 'avg %',    color: 'from-amber-500/15 to-orange-500/15 border-amber-500/30 text-amber-300' },
  { key: 'most_followed',  label: 'Most Followed',  icon: Users,         emoji: '👥', unit: 'followers', color: 'from-cyan-500/15 to-blue-500/15 border-cyan-500/30 text-cyan-300' },
  { key: 'longest_streak', label: 'Longest Streak', icon: Flame,         emoji: '🔥', unit: 'days',     color: 'from-orange-500/15 to-red-500/15 border-orange-500/30 text-orange-300' },
];

const RANK_MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };

function formatValue(value, category) {
  if (category === 'top_callers') return `${value}%`;
  if (category === 'best_returns') return `${value > 0 ? '+' : ''}${value}%`;
  if (category === 'longest_streak') return `${value}d`;
  return Number(value).toLocaleString();
}

function FollowBtn({ targetUserId, compact = false }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (user?.userId && targetUserId !== user.userId) {
      checkFollowing(targetUserId)
        .then(d => setFollowing(!!(d?.isFollowing || d?.following)))
        .catch(() => {})
        .finally(() => setBusy(false));
    } else {
      setBusy(false);
    }
  }, [targetUserId, user]);

  if (!user || targetUserId === user.userId) return null;

  return (
    <button
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setBusy(true);
        try {
          if (following) { await unfollowUser(targetUserId); setFollowing(false); }
          else            { await followUser(targetUserId);   setFollowing(true); }
        } catch {}
        setBusy(false);
      }}
      disabled={busy}
      className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg transition-all flex items-center gap-1 disabled:opacity-50 ${
        following
          ? 'bg-dark-700 text-gray-400 hover:bg-red-500/10 hover:text-red-400 border border-dark-600'
          : 'bg-violet-500/20 text-violet-300 hover:bg-violet-500/30 border border-violet-500/30'
      } ${compact ? '' : 'shrink-0'}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> :
       following ? '✓ Following' :
       <><UserPlus className="w-3 h-3" /> Follow</>}
    </button>
  );
}

export default function CommunityLeaderboardPage() {
  const [activeCategory, setActiveCategory] = useState('most_active');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    getLeaderboard(activeCategory)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [activeCategory]);

  const cat = CATEGORIES.find(c => c.key === activeCategory);
  const rankings = data?.rankings || [];
  const myRank = user?.userId
    ? rankings.findIndex(r => r.userId === user.userId)
    : -1;
  const top3 = rankings.slice(0, 3);
  const rest = rankings.slice(3);

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      {/* ─── Hero ──────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-dark-700/50 bg-gradient-to-br from-violet-500/10 via-dark-800/80 to-dark-900 p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-violet-500/30">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                Community Leaderboards
                <Sparkles className="w-5 h-5 text-amber-400" />
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">
                Top traders, callers, and community members — updated every 30 minutes.
              </p>
            </div>
          </div>

          {myRank >= 0 && (
            <div className="bg-dark-700/50 border border-violet-500/30 rounded-xl px-4 py-2">
              <p className="text-[10px] text-gray-500 uppercase">Your rank</p>
              <p className="text-lg font-bold text-violet-300">#{myRank + 1}</p>
            </div>
          )}
        </div>
      </div>

      {/* ─── Category pills ────────────────────────────────────────────── */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {CATEGORIES.map(c => {
          const isActive = activeCategory === c.key;
          const Icon = c.icon;
          return (
            <button key={c.key} onClick={() => setActiveCategory(c.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all border ${
                isActive
                  ? `bg-gradient-to-br ${c.color} shadow-lg`
                  : 'bg-dark-800 text-gray-400 hover:text-white border-dark-700/50 hover:border-dark-600'
              }`}>
              <Icon className="w-4 h-4" />
              {c.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
        </div>
      ) : rankings.length === 0 ? (
        <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-dark-700/50 flex items-center justify-center">
            <Users className="w-8 h-8 text-gray-600" />
          </div>
          <h3 className="text-white font-bold mb-2">No qualifying users yet</h3>
          <p className="text-gray-500 text-sm">Be the first to claim the top spot in {cat?.label}!</p>
        </div>
      ) : (
        <>
          {/* ─── Podium for top 3 ──────────────────────────────────────── */}
          {top3.length === 3 && (
            <div className="grid grid-cols-3 gap-4">
              {[top3[1], top3[0], top3[2]].map((entry, i) => {
                const realRank = [2, 1, 3][i];
                const name = safeName(entry);
                const grad = gradientFor(name);
                const isFirst = realRank === 1;
                const rb = ROLE_BADGE[entry.chatRole];
                return (
                  <Link
                    key={entry.userId}
                    to={`/community/profile/${entry.userId}`}
                    className={`relative rounded-2xl border p-5 text-center transition-all hover:-translate-y-1 hover:shadow-2xl ${
                      isFirst
                        ? 'bg-gradient-to-br from-amber-500/15 via-amber-500/5 to-dark-900 border-amber-500/40 shadow-xl shadow-amber-500/10 md:-mt-4'
                        : realRank === 2
                          ? 'bg-gradient-to-br from-slate-500/10 via-slate-500/5 to-dark-900 border-slate-500/30'
                          : 'bg-gradient-to-br from-amber-700/10 via-amber-700/5 to-dark-900 border-amber-700/30'
                    }`}
                  >
                    <div className="text-3xl mb-2">{RANK_MEDALS[realRank]}</div>
                    <div className={`w-16 h-16 mx-auto rounded-2xl border-2 border-dark-800 shadow-xl bg-gradient-to-br ${grad} flex items-center justify-center text-2xl font-bold text-white`}>
                      {(name || '?')[0].toUpperCase()}
                    </div>
                    <p className="text-sm font-bold text-white mt-3 truncate">{name}</p>
                    <div className="flex items-center justify-center gap-1 mt-1.5 flex-wrap">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${TIER_BADGE[entry.userTier] || TIER_BADGE.starter}`}>
                        {entry.userTier || 'starter'}
                      </span>
                      {rb && (
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${rb.cls}`}>
                          {rb.label}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-dark-700/50">
                      <p className={`text-2xl font-bold ${isFirst ? 'text-amber-400' : 'text-white'}`}>
                        {formatValue(entry.value, activeCategory)}
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">{cat?.unit}</p>
                    </div>
                    <div className="mt-3" onClick={(e) => e.preventDefault()}>
                      <FollowBtn targetUserId={entry.userId} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {/* ─── Remaining ranks (4+) ──────────────────────────────────── */}
          {rest.length > 0 && (
            <div className="bg-dark-800 rounded-2xl border border-dark-700/50 overflow-hidden">
              <div className="px-5 py-3 border-b border-dark-700/50 flex items-center justify-between">
                <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                  Rank #4 and below
                </p>
                <p className="text-[11px] text-gray-500">
                  {rankings.length} qualifying user{rankings.length === 1 ? '' : 's'}
                </p>
              </div>
              <div className="divide-y divide-dark-700/30">
                {rest.map((entry) => {
                  const name = safeName(entry);
                  const grad = gradientFor(name);
                  const rb = ROLE_BADGE[entry.chatRole];
                  const isMe = user?.userId === entry.userId;
                  return (
                    <Link
                      key={entry.userId}
                      to={`/community/profile/${entry.userId}`}
                      className={`flex items-center gap-3 px-5 py-3 transition-colors ${
                        isMe ? 'bg-violet-500/5 hover:bg-violet-500/10' : 'hover:bg-dark-700/30'
                      }`}
                    >
                      <span className="w-10 text-center font-bold text-sm text-gray-500">
                        #{entry.rank}
                      </span>
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${grad} flex items-center justify-center text-sm font-bold text-white flex-shrink-0`}>
                        {(name || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-white truncate">{name}</span>
                          {isMe && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/30">YOU</span>}
                          {rb && (
                            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${rb.cls}`}>{rb.label}</span>
                          )}
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${TIER_BADGE[entry.userTier] || TIER_BADGE.starter}`}>
                            {entry.userTier || 'starter'}
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-white">{formatValue(entry.value, activeCategory)}</p>
                        <p className="text-[9px] text-gray-500 uppercase">{cat?.unit}</p>
                      </div>
                      <div onClick={(e) => e.preventDefault()}>
                        <FollowBtn targetUserId={entry.userId} />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* ─── Cache note ────────────────────────────────────────────── */}
          {data?.computedAt && (
            <p className="text-[10px] text-gray-600 text-center">
              Computed {new Date(data.computedAt).toLocaleTimeString()} · refreshes every 30 minutes
            </p>
          )}
        </>
      )}
    </div>
  );
}
